/*
 * File: LocalStoragePrefs.js
 * Project: mp-mobile-app
 *
 * Created by jeddumire on 09/19/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Static class to fetch preferences that are currently stored in local storage
 *
 * No need to persist the data in a store because we do not need to observe
 * these changes or create another persisted source of truth outside of localstorage
 *
 * This will pull preferences by the logged in user's id.
 *
 * Stored object example using the users id as a key for the object:
 *
 * {
 *  "2": {
 *      preference1: true,
 *      preference2: "someString"
 *  },
 *  "14": {
 *      preference1: false,
 *      preference2: "anotherString"
 *  }
 * }
 */
class UserLocalStorage {
  keyName = "";

  constructor(keyName, userStore) {
    if (!keyName) throw new Error("Keyname is empty!");
    this.keyName = keyName;
    this.userStore = userStore;
  }

  /**
   * @return {String} Logged in user id
   */
  getUserId() {
    return this.userStore.userId.toString();
  }

  /**
   * Gets the preferences by keyname
   *
   * @param {String} pref
   */
  get(key) {
    let prefs = localStorage.getItem(this.keyName);

    if (!prefs) return null;

    let prefsObj = JSON.parse(prefs);
    let obj = prefsObj[this.getUserId()];
    if (!obj) return null;
    // return the object by key if the key exists in the object
    // otherwise just return the whole object
    if (!key) return obj;
    if (!obj) return null;
    else return key in obj ? obj[key] : null;
  }

  /**
   * Gets the whole object of the users pref
   *
   * @return {Object}
   */
  getAll() {
    let prefs = localStorage.getItem(this.keyName);
    if (!prefs) return null;
    return JSON.parse(prefs);
  }

  /**
   * Creates/updates a value in the users pref by key
   *
   * @param {String} key
   * @param {Mixed} value
   */
  set(key, value) {
    let prefs = this.getAll() || {};
    let userPrefs = prefs[this.getUserId()];

    if (!userPrefs) {
      prefs[this.getUserId()] = {};
      userPrefs = prefs[this.getUserId()];
    }
    userPrefs[key] = value;
    localStorage.setItem(this.keyName, JSON.stringify(prefs));
  }

  /**
   * Removes a pref
   */
  remove(key, deleteStorageItem = false) {
    if (deleteStorageItem) {
      localStorage.removeItem(this.keyName);
      return;
    }

    let prefs = this.getAll() || {};
    let userPrefs = prefs[this.getUserId()];
    if (!userPrefs) {
      return;
    }

    if (!key) {
      delete prefs[this.getUserId()];
    }

    if (key in userPrefs) delete userPrefs[key];

    if (JSON.stringify(prefs) !== JSON.stringify({}))
      localStorage.setItem(this.keyName, JSON.stringify(prefs));
    //delete the storage item from localstorage
    else localStorage.removeItem(this.keyName);
  }
}

export default UserLocalStorage;
